/**
 * engineNormalJson_Merge_Placeholder.ts - Placeholder processing for EngineNormalJson
 * Pure ES Module version
 */

import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { mergeDataJsonIntoHtmlJson } from '../../base/engine/htmlJsonMergeUtil.js';
import { EngineNormalJson } from './engineNormalJson.js';
import {
    TYPE_PLACEHOLDER_SKIP,
} from './engineNormalJson_Merge_Constants.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';

import { TemplateOperationKind } from '../../../../Assembler/dist/model/templateOperationKind.js';
/** Parameters for ProcessPlaceholder */
interface PlaceholderParams {
    key: string;
    keyType: string;
    keyName: string;
    value: unknown;
    appSite: string;
    currentTemplateName: string;
    appView: string | null;
    templateKey: string;
    operations: TemplateOperation[];
    jsonData: JsonObject;
    componentHtml: JsonObject | null;
    genComponentHtml: boolean;
    recursionDepth: number;
    contextJson: JsonObject | null;
    flowStack: string[] | null;
    configJson: JsonObject | null;
    clientMode: boolean;
}

/**
 * Process a placeholder entry in the template
 */
function ProcessPlaceholder(engine: EngineNormalJson, params: PlaceholderParams): { result: string; continue: boolean } {
    const {
        keyType,
        keyName,
        value,
        appSite,
        currentTemplateName,
        appView,
        templateKey,
        operations,
        jsonData,
        componentHtml,
        genComponentHtml,
        recursionDepth,
        contextJson,
        flowStack,
        configJson,
        clientMode
    } = params;

    // Start timing for placeholder processing
    const startPlaceholder = performance.now();

    // Extract component name from keyName (new format) or from value (old format fallback)
    const componentName = keyName || (typeof value === 'string' ? value.substring(2, value.length - 2) : '');
    const isPlaceholderSkipMarked = keyType === TYPE_PLACEHOLDER_SKIP || componentName.startsWith('!');
    const cleanComponentName = componentName.startsWith('!') ? componentName.substring(1) : componentName;

    // Check if this is a JSON key skip marker ({{!$Key}})
    if (cleanComponentName.startsWith('$')) {
        // This is a JSON key skip marker - remove it and log KeySkip operation
        const jsonKeyName = cleanComponentName.substring(1); // Remove "$" prefix to get "Title"

        // Replace with defaultSkipJson (skip marker)
        const result = engine._loader!.defaultSkipJson;

        // Log KeySkip operation
        operations.push({
            templateName: templateKey,
            kind: TemplateOperationKind.KeySkip,
            target: value as string,
            replacedWith: `${currentTemplateName}_${jsonKeyName}`,
            operations: [] as TemplateOperation[]
        } as TemplateOperation);

        // Skip timing/count for JsonKey skip markers to align with non-Json engines
        return { result, continue: true };
    }

    // Create immutable copy of flow stack with new item
    const newFlowStack = [...(flowStack || []), cleanComponentName];

    // For skip-marked placeholders in new format, the ! prefix is in TYPE not name
    // Add ! prefix for loader to recognize as skip request
    const componentNameForLoader = isPlaceholderSkipMarked && !componentName.startsWith('!')
        ? '!' + cleanComponentName
        : componentName;

    Logger.flow(`ProcessPlaceholder: placeholder=${componentName} depth=${recursionDepth}`, "NormalJson.TemplateRecursion");
    // Load component HtmlJson and DataJson using loader
    const startGetComp = performance.now();
    let { htmlJson: componentHtmlJson, dataJson: componentDataJson, resolvedTemplateName, resolvedAppSite: resolvedComponentAppSite } = engine._loader!.getTemplateWithView(appSite, currentTemplateName, componentNameForLoader, appView, newFlowStack, configJson, clientMode);
    engine._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetComp);
    engine._getTemplateCount++;
    Logger.flow(`ProcessPlaceholder: placeholder=${componentName} resolved=${resolvedTemplateName == null ? 'null' : resolvedTemplateName} resolvedSize=${componentHtmlJson ? componentHtmlJson.size : 0}`, "NormalJson.TemplateRecursion");

    // Update newFlowStack with resolved template name for correct inheritance lookup
    if (resolvedTemplateName && resolvedTemplateName !== cleanComponentName && newFlowStack.length > 0) {
        newFlowStack[newFlowStack.length - 1] = resolvedTemplateName;
    }

    let resultStr = '';

    if (componentHtmlJson) {
        // Create child operations list for this component
        const childOperations: TemplateOperation[] = [];

        // Use resolved template name for operations tracking
        const componentKey = `${resolvedComponentAppSite}_${resolvedTemplateName || cleanComponentName}`;

        // Apply dynamic view handlers BEFORE any merging - uses getViewJson when available (no conversion overhead)
        // Skip for skip-marked or missing templates
        const isSkippedOrMissing = resolvedTemplateName === engine._loader!.defaultSkipTemplateName || resolvedTemplateName === engine._loader!.defaultReplaceTemplateName;

        // For skip-marked placeholders, use ! prefix in component key for componentHtml
        const componentHtmlKey = isPlaceholderSkipMarked && !componentName.startsWith('!')
            ? '!' + cleanComponentName
            : componentName;

        // Pre-order: Set empty entry BEFORE recursion if genComponentHtml is enabled
        if (genComponentHtml && componentHtml) {
            componentHtml.set(componentHtmlKey, '');
        }

        let merged: string;
        // Only do JsonMerge and recursive processing for non-skip/non-missing templates (matches Normal engine)
        if (!isPlaceholderSkipMarked && !isSkippedOrMissing) {
            // DynamicView handlers applied after getTemplate above (Area 7: explicit engine call)

            // Merge component's DataJson into HtmlJson using utility (follows architecture).
            // Route KeyReplace/KeyMissing into staging lists so MergeTemplate can interleave them
            // in canonical phase order (after ArrayIteration, before ConditionalBlock).
            const jsonToMerge = componentDataJson ?? new JsonObject();
            const keyReplaceStage: TemplateOperation[] = [];
            const keyMissingStage: TemplateOperation[] = [];
            const startMerge = performance.now();
            const mergedComponentHtmlJson = mergeDataJsonIntoHtmlJson(componentHtmlJson!, jsonToMerge, componentKey, resolvedTemplateName || cleanComponentName, childOperations, engine._loader!.defaultReplaceHtml, false, contextJson, null, keyReplaceStage, keyMissingStage);
            engine._jsonMergeNanos += EngineNormalJson.msToNanos(performance.now() - startMerge);
            engine._jsonMergeCount++;

            // Recursively merge child template with merged HtmlJson (JsonKey entries already replaced)
            const mergeJsonData: JsonObject = componentDataJson ?? jsonData;
            merged = engine.mergeTemplate(
                mergedComponentHtmlJson,
                appSite,
                resolvedTemplateName || cleanComponentName,  // Use resolved template name, not placeholder name
                appView,
                componentKey,
                childOperations,
                mergeJsonData,
                componentHtml,
                newFlowStack,
                genComponentHtml,
                contextJson,
                configJson,
                recursionDepth + 1,
                null,
                keyReplaceStage,
                keyMissingStage,
                null,
                clientMode
            );
        } else if (isPlaceholderSkipMarked) {
            // Skip-marked placeholder - output DefaultSkipHtml
            merged = engine._loader!.defaultSkipHtml;
        } else {
            // Skipped/missing template - traverse HtmlJson entries via mergeTemplate
            merged = engine.mergeTemplate(
                componentHtmlJson,
                appSite,
                resolvedTemplateName || cleanComponentName,
                appView,
                componentKey,
                childOperations,
                jsonData,
                componentHtml,
                newFlowStack,
                genComponentHtml,
                contextJson,
                configJson,
                recursionDepth + 1,
                null,
                null,
                null,
                null,
                clientMode
            );
        }

        resultStr = merged;

        // Post-order: Update entry AFTER recursion if genComponentHtml is enabled
        if (genComponentHtml && componentHtml) {
            componentHtml.set(componentHtmlKey, merged);
        }

        // Determine operation type based on resolvedTemplateName
        let opType: TemplateOperationKind = TemplateOperationKind.ComponentReplace;
        if (isPlaceholderSkipMarked || resolvedTemplateName === engine._loader!.defaultSkipTemplateName) {
            opType = TemplateOperationKind.ComponentSkip;
        } else if (resolvedTemplateName === engine._loader!.defaultReplaceTemplateName) {
            opType = TemplateOperationKind.ComponentMissing;
        }

        // Include ! prefix in Target for skip markers, use _Skipped suffix for replacedWith
        const targetName = isPlaceholderSkipMarked ? `!${cleanComponentName}` : componentName;
        const replacedWithValue = isPlaceholderSkipMarked ? `${resolvedComponentAppSite}_Skipped` : componentKey;

        // Add operation tracking
        operations.push({
            templateName: templateKey,
            kind: opType,
            target: `{{${targetName}}}`,
            replacedWith: replacedWithValue,
            operations: childOperations
        } as TemplateOperation);
    } else {
        // Component not found
        // For skip-marked placeholders, output DefaultSkipHtml (Issue 3 from document)
        if (isPlaceholderSkipMarked) {
            resultStr = engine._loader!.defaultSkipHtml;

            // Add componentHtml entry with ! prefix for skip-marked placeholders
            const componentHtmlKey = '!' + cleanComponentName;
            if (genComponentHtml && componentHtml) {
                componentHtml.set(componentHtmlKey, engine._loader!.defaultSkipHtml);
            }

            operations.push({
                templateName: templateKey,
                kind: TemplateOperationKind.ComponentSkip,
                target: `{{!${cleanComponentName}}}`,
                replacedWith: `${appSite}_Skipped`,
                operations: [] as TemplateOperation[]
            } as TemplateOperation);
        } else {
            // Non-skip placeholder not found - keep placeholder as-is
            resultStr = value as string;

            operations.push({
                templateName: templateKey,
                kind: TemplateOperationKind.ComponentReplace,
                target: value as string,
                replacedWith: '',
                operations: [] as TemplateOperation[]
            } as TemplateOperation);
        }
    }

    // Capture timing at end of placeholder processing
    engine._placeholderProcessNanos += EngineNormalJson.msToNanos(performance.now() - startPlaceholder);
    engine._placeholderProcessCount++;

    return { result: resultStr, continue: false };
}

export const mergePlaceholderMethods = {
    ProcessPlaceholder(this: EngineNormalJson, params: PlaceholderParams): { result: string; continue: boolean } {
        return ProcessPlaceholder(this, params);
    }
};
